import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import storeApi from '../../../services/storeApi';
import dashboardApi from '../../../services/dashboardApi';
import bankAccountApi from '../../../services/bankAccountApi';
import terminalApi from '../../../services/terminalApi';
import companyApi from '../../../services/companyApi';
import { setStartAndEndDate } from '../../../utils/setStartAndEndDate';
import {
  DATE_OPTIONS,
  DEFAULT_DATE_RANGE,
  DEFAULT_TOP10_DATE_RANGE,
  resolveDateRange,
} from '../utils/date-presets';
import {
  buildAreaChartOptions,
  buildPieChartOptions,
  EMPTY_PIE_OPTIONS,
} from '../utils/chart-options';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.Ls.en.weekStart = 1;

const initialOrderInfo = {
  netPayout: 0,
  orderCount: 0,
  orderRefundTotal: 0,
  orderDaily: [],
  orderTop10: [],
  orderCountByTerminal: [],
  totalClickCount: 0,
  clickCount: 0,
};

const initialPie = {
  series: [],
  chartOptions: buildPieChartOptions([]),
};

export const useDashboardData = () => {
  const reduxCompany = useSelector((state) => state.companyReducer.selected);
  const userRoles = useSelector((state) => state.authReducer.roles);
  const isAdmin = userRoles.includes(100);

  const [globalCompany, setGlobalCompany] = useState(() =>
    isAdmin ? { name: 'All Companies', id: null } : reduxCompany,
  );

  const [search, setSearch] = useState({
    startdate: setStartAndEndDate(dayjs().startOf('day'), 'startdate', '0'),
    enddate: setStartAndEndDate(dayjs(), 'enddate', '0'),
    storeids: [],
  });

  const [selectedDateRange, setSelectedDateRange] = useState(DEFAULT_DATE_RANGE);
  const [isShowCustomRange, setIsShowCustomRange] = useState(false);

  const [selectedTop10DateRange, setSelectedTop10DateRange] = useState(
    DEFAULT_TOP10_DATE_RANGE,
  );
  const [top10Search, setTop10Search] = useState(() => {
    const range = resolveDateRange(DEFAULT_TOP10_DATE_RANGE);
    return {
      startdate: setStartAndEndDate(range.startdate, 'startdate', '0'),
      enddate: setStartAndEndDate(range.enddate, 'enddate', '0'),
    };
  });

  const [weekRange, setWeekRange] = useState({
    startOfWeek: setStartAndEndDate(dayjs().startOf('week'), 'startdate', '0'),
    endOfWeek: setStartAndEndDate(dayjs().endOf('week'), 'enddate', '0'),
  });

  const [weekRangeData, setWeekRangeData] = useState({ items: [], categories: [] });
  const [weekRangeChartType, setWeekRangeChartType] = useState('Sales');
  const [top10Type, setTop10Type] = useState('Sales');

  const [orderInformations, setOrderInformations] = useState(initialOrderInfo);
  const [pieChart, setPieChart] = useState(initialPie);

  const [companyList, setCompanyList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [storeWithMissingSettings, setStoreWithMissingSettings] = useState(null);
  const [bankAccount, setBankAccount] = useState([]);

  const [loading, setLoading] = useState({
    initialLoading: true,
    storeListLoading: true,
    companyListLoading: true,
    clickCountLoading: true,
    graphicLoading: true,
  });

  const [, setNotf] = useState({ display: false, type: 'error', message: '' });
  const flashError = useCallback(
    (error) => setNotf({ display: true, type: 'error', message: error }),
    [],
  );

  const onDateChange = useCallback((dateString) => {
    setSearch((prev) => ({
      ...prev,
      startdate: setStartAndEndDate(dateString[0], 'startdate', '0'),
      enddate: setStartAndEndDate(dateString[1], 'enddate', '0'),
    }));
  }, []);

  const onDateRangeSelectChange = useCallback((selected) => {
    if (!selected) return;
    if (selected === 'customRange') {
      setIsShowCustomRange(true);
      return;
    }
    setIsShowCustomRange(false);
    const range = resolveDateRange(selected);
    if (!range) return;
    setSearch((prev) => ({
      ...prev,
      startdate: setStartAndEndDate(range.startdate, 'startdate', '0'),
      enddate: setStartAndEndDate(range.enddate, 'enddate', '0'),
    }));
  }, []);

  const onSelectStore = useCallback((storeid) => {
    setSearch((prev) => ({ ...prev, storeids: storeid ? [storeid] : [] }));
  }, []);

  const onTop10DateRangeSelectChange = useCallback((selected) => {
    if (!selected || selected === 'customRange') return;
    const range = resolveDateRange(selected);
    if (!range) return;
    setSelectedTop10DateRange(selected);
    setTop10Search({
      startdate: setStartAndEndDate(range.startdate, 'startdate', '0'),
      enddate: setStartAndEndDate(range.enddate, 'enddate', '0'),
    });
  }, []);

  const handleWeekChange = useCallback((startOfWeek, endOfWeek) => {
    setWeekRange({ startOfWeek, endOfWeek });
  }, []);

  useEffect(() => {
    if (!isAdmin) setGlobalCompany(reduxCompany);
  }, [reduxCompany, isAdmin]);

  useEffect(() => {
    let cancelled = false;
    setLoading((prev) => ({ ...prev, companyListLoading: true, initialLoading: true }));
    companyApi
      .lookup()
      .then((res) => {
        if (cancelled) return;
        const companies = res.data?.succeeded && res.data?.data ? res.data.data : [];
        setCompanyList(companies);
      })
      .catch((error) => {
        if (cancelled) return;
        flashError(error);
        setCompanyList([]);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading((prev) => ({ ...prev, companyListLoading: false }));
        }
      });

    if (!isAdmin) {
      const companyId = reduxCompany?.id;
      if (companyId) {
        bankAccountApi
          .list(`?companyid=${companyId}&PageSize=1`)
          .then((res) => {
            if (cancelled) return;
            if (res.data.data.length > 0) setBankAccount(res.data.data);
          })
          .catch((error) => {
            if (!cancelled) flashError(error);
          });
      }
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading((prev) => ({ ...prev, storeListLoading: true }));

    if (!globalCompany.id) {
      setStoreList([]);
      setStoreWithMissingSettings(null);
      setLoading((prev) => ({ ...prev, storeListLoading: false }));
      return undefined;
    }

    storeApi
      .lookupForDashboard(globalCompany.id)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.succeeded && res.data?.data ? res.data.data : null;
        if (data?.stores?.length > 0) {
          setStoreList(data.stores);
          setStoreWithMissingSettings(data.storeWithMissingSettings ?? null);
        } else {
          setStoreList([]);
          setStoreWithMissingSettings(null);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        flashError(error);
        setStoreList([]);
        setStoreWithMissingSettings(null);
      })
      .finally(() => {
        if (!cancelled) setLoading((prev) => ({ ...prev, storeListLoading: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [globalCompany.id, flashError]);

  useEffect(() => {
    let cancelled = false;
    setLoading((prev) => ({ ...prev, graphicLoading: true }));

    if (!isAdmin && !globalCompany.id) {
      setWeekRangeData({ items: [], categories: [] });
      setLoading((prev) => ({ ...prev, graphicLoading: false }));
      return undefined;
    }

    const query = {
      companyid: globalCompany.id,
      startdate: setStartAndEndDate(weekRange.startOfWeek, 'startdate', '0'),
      enddate: setStartAndEndDate(weekRange.endOfWeek, 'enddate', '0'),
    };

    dashboardApi
      .getOrdersDaily(query)
      .then((res) => {
        if (cancelled) return;
        if (!res.data.data) {
          setWeekRangeData({ items: [], categories: [] });
          return;
        }

        const today = dayjs();
        const dateRange = [];
        let cursor = dayjs(weekRange.startOfWeek);
        const endDate = dayjs(weekRange.endOfWeek);

        while (cursor.isSameOrBefore(endDate, 'day')) {
          dateRange.push(cursor.format('YYYY-MM-DD'));
          cursor = cursor.add(1, 'day');
        }

        const dataMap = {};
        res.data.data.forEach((item) => {
          dataMap[item.date] = item;
        });

        const completeData = dateRange.map((date) => {
          if (dataMap[date]) return dataMap[date];
          const isAfterToday = dayjs(date).isAfter(today);
          return {
            date,
            total: isAfterToday ? null : 0,
            qty: isAfterToday ? null : 0,
          };
        });

        const categories = dateRange.map((date) => dayjs(date).format('ddd'));
        setWeekRangeData({ items: completeData, categories });
      })
      .catch((error) => {
        if (!cancelled) flashError(error);
      })
      .finally(() => {
        if (!cancelled) setLoading((prev) => ({ ...prev, graphicLoading: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [weekRange, globalCompany.id, flashError, isAdmin]);

  const storeIdsKey = search.storeids.join(',');

  useEffect(() => {
    let cancelled = false;
    const companyId = globalCompany.id;

    if (!isAdmin && !companyId) {
      setOrderInformations(initialOrderInfo);
      setPieChart(initialPie);
      setLoading((prev) => ({
        ...prev,
        initialLoading: false,
        clickCountLoading: false,
      }));
      return undefined;
    }

    const query = { ...search, companyid: companyId };

    setLoading((prev) => ({ ...prev, initialLoading: true, clickCountLoading: true }));

    const promises = [];

    promises.push(
      dashboardApi
        .getNetPayout(query)
        .then((res) => {
          if (cancelled) return;
          setOrderInformations((prev) => ({ ...prev, netPayout: res.data.data || 0 }));
        })
        .catch(() => {}),
    );

    promises.push(
      dashboardApi
        .getOrderCount(query)
        .then((res) => {
          if (cancelled) return;
          setOrderInformations((prev) => ({ ...prev, orderCount: res.data.data || 0 }));
        })
        .catch(() => {}),
    );

    promises.push(
      dashboardApi
        .getOrderRefundTotal(query)
        .then((res) => {
          if (cancelled) return;
          setOrderInformations((prev) => ({
            ...prev,
            orderRefundTotal: res.data.data || 0,
          }));
        })
        .catch(() => {}),
    );

    promises.push(
      dashboardApi
        .getOrderCountByTerminal(query)
        .then((res) => {
          if (cancelled) return;
          const data = res.data.data || [];
          if (data.length > 0) {
            setOrderInformations((prev) => ({ ...prev, orderCountByTerminal: data }));
            const labels = data.map((item, idx) => {
              const raw = (item.terminal ?? '').toString().trim();
              return raw.length > 0 ? raw : `Channel ${idx + 1}`;
            });
            setPieChart({
              series: data.map((item) => item.count),
              chartOptions: buildPieChartOptions(labels),
            });
          } else {
            setOrderInformations((prev) => ({ ...prev, orderCountByTerminal: [] }));
            setPieChart({
              series: [100],
              chartOptions: EMPTY_PIE_OPTIONS('No data'),
            });
          }
        })
        .catch(() => {}),
    );

    let totalClickQuery = `?pageSize=1000&addclickcount=true`;
    if (!isAdmin) totalClickQuery += `&companyid=${companyId}`;

    promises.push(
      terminalApi
        .getClickCount(totalClickQuery)
        .then((res) => {
          if (cancelled) return;
          setOrderInformations((prev) => ({
            ...prev,
            totalClickCount: res.data || 0,
          }));
        })
        .catch(() => {}),
    );

    let clickQuery = `?pageSize=1000&startdate=${search.startdate}&enddate=${search.enddate}&addclickcount=true`;
    if (!isAdmin) clickQuery += `&companyid=${companyId}`;
    else if (companyId) clickQuery += `&companyid=${companyId}`;
    // Apply the Stores filter to Kiosk Interactions too, so selecting a store
    // narrows the count instead of always summing the whole company.
    if (search.storeids?.length) clickQuery += `&storeid=${search.storeids[0]}`;

    promises.push(
      terminalApi
        .getClickCount(clickQuery)
        .then((res) => {
          if (cancelled) return;
          setOrderInformations((prev) => ({ ...prev, clickCount: res.data || 0 }));
        })
        .catch(() => {}),
    );

    Promise.allSettled(promises).then(() => {
      if (!cancelled) {
        setLoading((prev) => ({
          ...prev,
          initialLoading: false,
          clickCountLoading: false,
        }));
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.startdate, search.enddate, storeIdsKey, globalCompany.id, isAdmin]);

  // Separate effect for Top 10 — uses its own date range (default: Last 30 days)
  useEffect(() => {
    let cancelled = false;
    if (!isAdmin && !globalCompany.id) {
      setOrderInformations((prev) => ({ ...prev, orderTop10: [] }));
      return undefined;
    }
    const query = {
      startdate: top10Search.startdate,
      enddate: top10Search.enddate,
      storeids: search.storeids,
      companyid: globalCompany.id,
    };
    dashboardApi
      .getOrderTop10(query)
      .then((res) => {
        if (cancelled) return;
        setOrderInformations((prev) => ({
          ...prev,
          orderTop10: res.data.data || [],
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top10Search.startdate, top10Search.enddate, storeIdsKey, globalCompany.id, isAdmin]);

  const chart = useMemo(() => {
    const { items, categories } = weekRangeData;
    const fallbackCategories = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    if (items.length === 0) {
      return {
        series: [{ name: weekRangeChartType, data: [] }],
        options: buildAreaChartOptions({
          categories: fallbackCategories,
          mode: weekRangeChartType,
          isEmpty: true,
        }),
      };
    }

    const data = items.map((item) =>
      item[weekRangeChartType === 'Sales' ? 'total' : 'qty'],
    );
    const maxVal = data.reduce((acc, v) => (v != null && v > acc ? v : acc), 0);

    return {
      series: [{ name: weekRangeChartType, data }],
      options: buildAreaChartOptions({
        categories,
        dates: items.map((item) => item.date),
        mode: weekRangeChartType,
        isEmpty: maxVal === 0,
      }),
    };
  }, [weekRangeData, weekRangeChartType]);

  const orderedTop10Items = useMemo(() => {
    const key = top10Type === 'Sales' ? 'total' : 'qty';
    return orderInformations.orderTop10
      .filter((x) => x.product)
      .sort((a, b) => b[key] - a[key]);
  }, [orderInformations.orderTop10, top10Type]);

  return {
    isAdmin,
    userRoles,
    companyList,
    storeList,
    storeWithMissingSettings,
    bankAccount,
    globalCompany,
    setGlobalCompany,
    search,
    setSearch,
    selectedDateRange,
    setSelectedDateRange,
    isShowCustomRange,
    setIsShowCustomRange,
    onDateChange,
    onDateRangeSelectChange,
    onSelectStore,
    weekRange,
    handleWeekChange,
    weekRangeData,
    weekRangeChartType,
    setWeekRangeChartType,
    chart,
    orderInformations,
    pieChart,
    orderedTop10Items,
    top10Type,
    setTop10Type,
    selectedTop10DateRange,
    onTop10DateRangeSelectChange,
    loading,
    dateOptions: DATE_OPTIONS,
  };
};
